import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { ApplicationContext, ApplicationProgress } from './ApplicationContext';
import { studentApplicationInitialValues, type StudentApplicationValues } from '../../student/application/StudentApplication';
import { schema } from '../../student/application/schema';
import { ZodArray, ZodBoolean, ZodDiscriminatedUnion, ZodEffects, ZodNullable, ZodObject, ZodOptional, ZodRawShape, ZodString, ZodTypeAny } from 'zod';

// Get the nested value of an object, given an array of keys
const getNestedValue = (obj: object, path: string[]): object[] => {
  const value = path.reduce((acc, key) => acc?.[key as keyof typeof obj], obj);
  return Array.isArray(value) ? value : [];
};

export const ApplicationProvider = ({ children }: PropsWithChildren) => {
  const [applicationData, setApplicationData] = useState<StudentApplicationValues>(studentApplicationInitialValues);
  const [applicationProgress, setApplicationProgress] = useState<ApplicationProgress<StudentApplicationValues>>();
  const [displayErrors, setDisplayErrors] = useState(false);

  const triggerErrors = useCallback(() => setDisplayErrors(true), []);

  const setApplicationPageData = useCallback((page: keyof StudentApplicationValues, values: StudentApplicationValues[keyof StudentApplicationValues]) => {
    setApplicationData((prevState) => {
      return { ...prevState, [page]: values };
    });
  }, []);

  /**
   * Recursively counts the total required fields in a given schema
   *
   * @param schema - A Zod object of any kind
   * @param schema - An array of tuples where the first item is the page key and the second is a ZodObject schema.
   * @returns The total number of required fields
   */
  const countRequiredFields = useCallback(
    (schema: ZodObject<ZodRawShape> | ZodArray<ZodTypeAny> | ZodTypeAny, keys: string[] = []): number => {
      if (schema instanceof ZodObject) {
        const shape = schema.shape as Record<string, ZodTypeAny>;
        return Object.entries(shape).reduce((acc: number, [key, value]) => acc + countRequiredFields(value, [...keys, key]), 0);
      }

      if (schema instanceof ZodArray) {
        let arrayLength = 0;

        // The amount of required fields should be calculated based on the length of the array.
        // e.g. if an array has 3 items that each have 5 required values, we need to count 15 required fields.
        if (applicationData && getNestedValue(applicationData, keys)?.length > 0) {
          arrayLength = getNestedValue(applicationData, keys)?.length;
        }

        // If the array is just an array of strings, we set it to 1
        // (this is mostly needed for checkboxsets where the value is an array of strings)
        if (schema.element instanceof ZodString) {
          arrayLength = 1;
        }

        return countRequiredFields(schema.element, [...keys]) * arrayLength;
      }

      if (schema instanceof ZodEffects) {
        return countRequiredFields(schema.innerType(), keys);
      }

      if (schema instanceof ZodDiscriminatedUnion) {
        const options = Array.from(schema.options.values());
        // Count fields in each variant
        const counts = options.map((option) => countRequiredFields(option as ZodTypeAny, keys));

        // Return the maximum count (to reflect worst case scenario).
        return Math.max(...counts) - 1;
      }

      // Ignore optional fields
      if (schema instanceof ZodOptional || schema instanceof ZodNullable || schema instanceof ZodBoolean) {
        return 0;
      }

      // Leaf field (e.g. string, number, date, etc.)
      return 1;
    },
    [applicationData],
  );

  /**
   * Generates a progress object for each page and its fields.
   *
   * @returns An object with a top-level progress key and nested progress keys for each page and each of its sections.
   */
  const getProgress = useCallback((): ApplicationProgress<StudentApplicationValues> => {
    // Parse full schema and get total field amount
    const result = schema.safeParse(applicationData);
    const pages = Object.entries(schema.shape);

    if (!result.success) {
      // Get amount of errors and calculate %
      const fieldsWithErrors = result.error.issues.length;
      const totalFields = countRequiredFields(schema);
      const totalProgress = Math.floor(((totalFields - fieldsWithErrors) / totalFields) * 100);
      const pagesProgress: Partial<ApplicationProgress<StudentApplicationValues>> = {};

      pages.forEach(([key, schema]) => {
        // Parse the schema for all pages
        const page = key as keyof StudentApplicationValues;
        const result = schema.safeParse(applicationData?.[page]);
        const sections = Object.entries(schema.shape);
        if (!result.success) {
          // Get amount of errors and calculate % per page
          const fieldsWithErrors = result.error.issues.length;
          const totalFields = countRequiredFields(schema, [page]);
          const sectionsProgress: Partial<ApplicationProgress<StudentApplicationValues>> = {};

          sections.forEach(([section, schema]) => {
            // Parse the schema for all sections

            //@ts-expect-error TODO: find a way to type this once HF and LC types have been implemented
            const result = schema.safeParse(applicationData?.[page][section]);

            if (!result.success) {
              // Get amount of errors and calculate % per section
              const fieldsWithErrors = result.error.issues.length;
              const totalFields = countRequiredFields(schema, [page, section]);
              const sectionProgress = Math.floor(((totalFields - fieldsWithErrors) / totalFields) * 100);

              //@ts-expect-error TODO: find a way to type this once HF and LC types have been implemented
              sectionsProgress[section] = { progress: sectionProgress };
            } else {
              //@ts-expect-error TODO: find a way to type this once HF and LC types have been implemented
              sectionsProgress[section] = { progress: 100 };
            }
          });

          const pageProgress = Math.floor(((totalFields - fieldsWithErrors) / totalFields) * 100);
          pagesProgress[page] = { ...sectionsProgress, progress: pageProgress };
        } else {
          pagesProgress[page] = {
            progress: 100,
            // Return { progress: 100 } for all the sections within that page
            ...Object.fromEntries(sections.map(([key]) => [key, { progress: 100 }])),
          };
        }
      });

      // Return progress for the individual pages + the total progress
      return { ...pagesProgress, progress: totalProgress };
    } else {
      // If the schema parsed successfully, it means there are no missing fields or errors, so we return 100%
      return {
        progress: 100,
        // Return { progress: 100 } for all pages and sections within the schema
        ...Object.fromEntries(
          pages.map(([key, schema]) => {
            const innerKeys = Object.keys(schema.shape);
            const nested = Object.fromEntries(innerKeys.map((k) => [k, { progress: 100 }]));
            return [key, { progress: 100, ...nested }];
          }),
        ),
      };
    }
  }, [applicationData, countRequiredFields]);

  useEffect(() => {
    if (applicationData) {
      const progress = getProgress();
      setApplicationProgress(progress);
    }
  }, [applicationData, getProgress]);

  return (
    <ApplicationContext.Provider value={{ setApplicationData, setApplicationPageData, applicationData, applicationProgress, triggerErrors, displayErrors }}>
      {children}
    </ApplicationContext.Provider>
  );
};
